import React, { LegacyRef, useEffect, useRef, useState } from "react";
import {
  ReportContent,
  ReportSkillSummary,
  StudentBasic,
  students_gender,
  OrderedRulesTemplates,
} from "@mindprint-learning/api-lib";
import { FaPrint } from "react-icons/fa6";

import { MindPrintProfileProps } from "./MindPrintProfile.types";
import Container from "../../atoms/Container/Container";
import Button from "../../atoms/Button/Button";
import Divider from "../../atoms/Divider/Divider";
import Chart from "./ui/Chart";
import NextSteps from "./ui/NextSteps";
import { ButtonSizes, ButtonTypes } from "../../atoms/Button/Button.types";
import combineSortRules from "./MindPrintProfile.helpers";

import "./MindPrintProfile.scss";
import "../../tokens/alignment.scss";

const MindPrintProfile: React.FC<any> = ({ data, trial }) => {
  const customRules = data.custom_rules;
  const engineRules = data.rules;

  // combine/sort engine & custom rules
  const ruleList = combineSortRules(engineRules, customRules);

  const strengths = ruleList.strengths;
  const needs = ruleList.needs;
  const recommendations = ruleList.recommendations;

  const studentName = data.student.first_name || data.student.last_name;

  const content: Array<ContentProps> = [
    {
      data: data.template.disclaimer,
      level: ContentLevels.level3,
    },
    {
      data: { title: "Summary Results", html_content: [] },
      children: <Chart data={data.skills} studentName={studentName} />,
      level: ContentLevels.level1,
    },
    {
      data: {
        title: `${studentName}'s Stronger Skills`,
        html_content: strengths.map((value) =>
          value.isFootnote
            ? `<div class='-footnote'>${value.content}</div>`
            : value.label
            ? `<div><b>${value.label}:</b> ${value.content}</div>`
            : `<div>${value.content}</div>`
        ),
      },
      level: ContentLevels.level2,
      hide: trial,
    },
    {
      data: {
        title: `${studentName}'s Skills to Support`,
        html_content: needs.map((value) =>
          value.isFootnote
            ? `<div class='-footnote'>${value.content}</div>`
            : value.label
            ? `<div><b>${value.label}:</b> ${value.content}</div>`
            : `<div>${value.content}</div>`
        ),
      },
      level: ContentLevels.level2,
      hide: trial,
    },
    {
      data: {
        title: `Recommendations for ${studentName}`,
        html_content: recommendations.map((value) =>
          value.isFootnote
            ? `<div class='-footnote'>${value.content}</div>`
            : value.label
            ? `<div><b>${value.label}:</b> ${value.content}</div>`
            : `<div>${value.content}</div>`
        ),
      },
      level: ContentLevels.level2,
      hide: trial,
    },
    {
      data: {
        title: ``,
        html_content: [
          "This is an adult trial assessment. For a student assessment, the <b>Student Summary and Recommendations</b> section would be found here.",
        ],
      },
      level: ContentLevels.level2,
      hide: !trial,
    },
    {
      data: data.template.next_steps,
      level: ContentLevels.level1,
      children: <NextSteps data={data.template.next_steps} />,
    },
    {
      data: data.template.performance_by_skill.content,
      level: ContentLevels.level1,
    },
    {
      data: data.template.performance_by_skill.speed.content,
      level: ContentLevels.level2,
    },
    {
      data: data.template.performance_by_skill.speed.visual_motor_speed.content,
      summary:
        data.template.performance_by_skill.speed.visual_motor_speed.summary,
      level: ContentLevels.level3,
    },
    {
      data: data.template.performance_by_skill.speed.processing_speed.content,
      summary:
        data.template.performance_by_skill.speed.processing_speed.summary,
      level: ContentLevels.level3,
    },
    {
      data: data.template.performance_by_skill.executive_functions.content,
      level: ContentLevels.level2,
    },
    {
      data: data.template.performance_by_skill.executive_functions.attention
        .content,
      summary:
        data.template.performance_by_skill.executive_functions.attention
          .summary,
      level: ContentLevels.level3,
    },
    {
      data: data.template.performance_by_skill.executive_functions
        .working_memory.content,
      summary:
        data.template.performance_by_skill.executive_functions.working_memory
          .summary,
      level: ContentLevels.level3,
    },
    {
      data: data.template.performance_by_skill.executive_functions
        .flexible_thinking.content,
      summary:
        data.template.performance_by_skill.executive_functions.flexible_thinking
          .summary,
      level: ContentLevels.level3,
    },
    {
      data: data.template.performance_by_skill.complex_reasoning.content,
      level: ContentLevels.level2,
    },
    {
      data: data.template.performance_by_skill.complex_reasoning
        .verbal_reasoning.content,
      summary:
        data.template.performance_by_skill.complex_reasoning.verbal_reasoning
          .summary,
      level: ContentLevels.level3,
    },
    {
      data: data.template.performance_by_skill.complex_reasoning
        .abstract_reasoning.content,
      summary:
        data.template.performance_by_skill.complex_reasoning.abstract_reasoning
          .summary,
      level: ContentLevels.level3,
    },
    {
      data: data.template.performance_by_skill.complex_reasoning
        .spatial_perception.content,
      summary:
        data.template.performance_by_skill.complex_reasoning.spatial_perception
          .summary,
      level: ContentLevels.level3,
    },
    {
      data: data.template.performance_by_skill.memory.content,
      level: ContentLevels.level2,
    },
    {
      data: data.template.performance_by_skill.memory.verbal_memory.content,
      summary: data.template.performance_by_skill.memory.verbal_memory.summary,
      level: ContentLevels.level3,
    },
    {
      data: data.template.performance_by_skill.memory.visual_memory.content,
      summary: data.template.performance_by_skill.memory.visual_memory.summary,
      level: ContentLevels.level3,
    },
    {
      data: data.template.translate_results.content,
      level: ContentLevels.level1,
    },
    {
      data: data.template.translate_results.improve_academic_performance,
      level: ContentLevels.level2,
    },
    {
      data: data.template.translate_results.support_cognitive_skills,
      level: ContentLevels.level2,
    },
    {
      data: data.template.understand_anxiety_mood,
      level: ContentLevels.level1,
    },
    {
      data: data.template.sharing_results,
      level: ContentLevels.level1,
    },
  ].map((value) => {
    const r: LegacyRef<HTMLDivElement> = useRef(null);
    return {
      level: value.level,
      data: value.data,
      student: data.student,
      summary: value.summary,
      children: value.children,
      contentRef: r,
      hide: value.hide,
    };
  });

  return (
    <div
      data-testid="MindPrintProfile"
      className="mp-templates-mindprintprofile--main"
    >
      {data.student && content && (
        <>
          <Container>
            <div className="header">
              <div className="header-info">
                <h1>
                  {data.student.first_name} {data.student.last_name}
                </h1>
                <p className="header-text">MindPrint Profile</p>
                {data.assessment?.completed_at && (
                  <p className="header-text">
                    Test Date: {data.assessment.completed_at}
                  </p>
                )}
              </div>
              <div className="hideForPrint">
                <Button
                  primary={true}
                  label={
                    <span className="icon-text">
                      <FaPrint />
                      <p>Print</p>
                    </span>
                  }
                  size={ButtonSizes.icon}
                  type={ButtonTypes.neutral}
                  onClick={() => window && window.print()}
                />
              </div>
            </div>
            <Divider />
          </Container>

          <Container>
            <div className="main-wrapper">
              <div className="content-wrapper">
                {content.map((value, index) => {
                  return (
                    <Content
                      key={`content-${index}`}
                      contentRef={value.contentRef}
                      data={value.data}
                      level={value.level}
                      student={data.student}
                      summary={value.summary}
                      hide={value.hide}
                    >
                      {value.children}
                    </Content>
                  );
                })}
              </div>
            </div>
          </Container>
        </>
      )}
    </div>
  );
};

export default MindPrintProfile;

// interface ContentListItem {
//   level: ContentLevels;
//   contentRef: React.RefObject<HTMLDivElement>;
//   data: ReportContent;
// }

const Content = ({
  contentRef,
  data,
  student,
  level,
  summary,
  children,
  hide,
}: ContentProps) => {
  return (
    <>
      {hide !== true && (
        <div className={`content-container level-${level}`} ref={contentRef}>
          {level === 1 && (
            <>
              <h2>{data.title}</h2>
            </>
          )}
          {level === 2 && (
            <>
              <h3>{data.title}</h3>
              <Divider />
            </>
          )}
          {level === 3 && (
            <>
              <h4>{data.title}</h4>
              <Divider />
            </>
          )}

          <div className="content-column-wrapper">
            <div className={`content level-${level}`}>
              {data.html_content.map((value, index) => {
                return (
                  <ContentBlock
                    key={`content-block-${index}`}
                    value={value}
                    student={student}
                  />
                );
              })}
              {children && children}
            </div>
            {summary && (
              <div className="summary-content">
                <div className="summary-content-wrapper">
                  <div className={`summary-block-title ${summary.score_range}`}>
                    {summary.title}
                  </div>

                  <div className="summary-block">
                    {summary.html_content.map((value, index) => {
                      return (
                        <ContentBlock
                          key={`content-block-${index}`}
                          value={value}
                          student={student}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

enum ContentLevels {
  level1 = 1,
  level2 = 2,
  level3 = 3,
}

interface ContentProps {
  contentRef?: React.RefObject<HTMLDivElement>;
  data: ReportContent;
  student: StudentBasic;
  level: ContentLevels;
  summary?: ReportSkillSummary;
  children?: React.ReactNode;
  hide?: boolean;
}

const ContentBlock = ({ value, student }: ContentBlockProps) => {
  const pn = student.gender === students_gender.male ? "his" : "her";
  const pn2 = student.gender === students_gender.male ? "him" : "her";
  const pn3 = student.gender === students_gender.male ? "he" : "she";

  const content = value
    .replace(
      new RegExp(/{{first-name}}/g),
      student.first_name || student.last_name
    )
    .replace(new RegExp(/{{his-her}}/g), pn)
    .replace(new RegExp(/{{him-her}}/g), pn2)
    .replace(new RegExp(/{{he-she}}/g), pn3);

  return (
    <div
      className="content-block"
      dangerouslySetInnerHTML={{
        __html: content,
      }}
    />
  );
};

interface ContentBlockProps {
  value: string;
  student: StudentBasic;
}

const Outline = ({ items }: OutlineProps) => {
  return (
    <div className="outline-wrapper">
      <div className="outline-container">
        {items.map((r, index) => {
          return (
            <OutlineItem
              key={`ref-${index}`}
              pageRef={r.contentRef}
              level={r.level}
            >
              {r.data.title}
            </OutlineItem>
          );
        })}
      </div>
    </div>
  );
};

interface OutlineProps {
  items: Array<ContentProps>;
}

const OutlineItem = ({ pageRef, level, children }: OutlineItemProps) => {
  return (
    <div
      className={`outline level-${level}`}
      onClick={() => {
        pageRef?.current?.scrollIntoView({ behavior: "smooth" });
      }}
    >
      {children}
    </div>
  );
};

interface OutlineItemProps {
  children: React.ReactNode;
  pageRef: React.RefObject<HTMLDivElement> | undefined;
  level: 1 | 2 | 3;
}

/*

PHP For chart footnote
   @if ($LearningProfile->newGetScores('strength', 'high'))
                <li><img src="/images/sprites/ui/icon-star-gray-16.png"> {{ $LearningProfile->customer->first_name }} performed in the top 5% of {{ $LearningProfile->customer->pronoun_posessive }} peer group</li>
            @endif

            @if ($LearningProfile->newGetScores('weakness', 'low'))
                <li><img src="/images/sprites/ui/icon-square-gray-16.png"> {{ $LearningProfile->customer->first_name }} performed in the bottom 5% of {{ $LearningProfile->customer->pronoun_posessive }} peer group</li>
            @endif
*/
